import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Truck, ShieldCheck, Sparkles, Check } from "lucide-react";
import { PRODUCT, variantsData, ingredientsData, benefitsData, stepsData, productImages } from "../lib/data";
import { Reveal } from "../components/Reveal";
import { Icon } from "../components/Icon";
import { FaqAccordion } from "../components/FaqAccordion";
import { Reviews } from "../components/Reviews";
import { StockBadge } from "../components/StockBadge";
import { useCart } from "../context/CartContext";
import { useTheme } from "../context/ThemeContext";
import { useLang } from "../context/LanguageContext";
import { useImages } from "../context/ContentContext";
import { toast } from "sonner";
import { api } from "../lib/api";
import PaymentTrust from "../components/PaymentTrust";

const eur = (n) => n.toFixed(2).replace(".", ",") + " €";

export default function Product({ fixedUniverse = null }) {
  const [active, setActive] = useState(0);
  const [q, setQ] = useState(1);
  const [variantKey, setVariantKey] = useState("single");
  const [catalog, setCatalog] = useState([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [publishedReviews, setPublishedReviews] = useState([]);
  const { add, items } = useCart();
  const { universe, setUniverse } = useTheme();
  const effectiveUniverse = fixedUniverse || universe;
  const { t, lang } = useLang();
  const nav = useNavigate();
  const IMG = useImages();
  const imgs = productImages(effectiveUniverse, IMG);
  const GALLERY = imgs.gallery;
  const BASE_VARIANTS = variantsData(lang);
  // Le sérum simple possède désormais un stock indépendant selon l'univers.
  const VARIANTS = {
    ...BASE_VARIANTS,
    single: {
      ...BASE_VARIANTS.single,
      key: effectiveUniverse === "femme" ? "serum_femme" : "serum_homme",
      label: effectiveUniverse === "femme" ? "Sérum Femme" : "Sérum Homme",
      image: imgs.single,
      universe: effectiveUniverse,
    },
    duo: {
      ...BASE_VARIANTS.duo,
      key: effectiveUniverse === "femme" ? "serum_duo_femme" : "serum_duo_homme",
      label: effectiveUniverse === "femme" ? "Duo Femme" : "Duo Homme",
      image: imgs.duo,
      universe: effectiveUniverse,
    },
  };
  const INGREDIENTS = ingredientsData(lang);
  const BENEFITS = benefitsData(lang);
  const STEPS = stepsData(lang);
  const variant = VARIANTS[variantKey];
  const variantImg = variantKey === "duo" ? imgs.duo : imgs.single;
  const stockByKey = Object.fromEntries(catalog.map((p) => [p.lookup_key, p]));
  const currentProduct = stockByKey[variant.key];
  const stockKnown = catalogLoaded && !!currentProduct;
  const stock = Number(currentProduct?.stock ?? 0);
  // Ne jamais considérer un produit comme épuisé avant la réponse de l'API stock.
  // Cela évite le faux « Épuisé » visible brièvement lors d'une arrivée directe (Instagram, etc.).
  const out = stockKnown && stock <= 0;

  useEffect(() => {
    if (fixedUniverse) setUniverse(fixedUniverse);
  }, [fixedUniverse, setUniverse]);

  useEffect(() => {
    const isFemme = effectiveUniverse === "femme";
    const genderLabel = isFemme ? "Femme" : "Homme";
    const path = isFemme ? "/serum-femme" : "/serum-homme";
    const canonical = `${window.location.origin}${path}`;
    const productName = `LUXE ORYVEA PREMIUM — Sérum Capillaire ${genderLabel}`;
    const description = isFemme
      ? "Sérum capillaire LUXE ORYVEA PREMIUM pour femme — soin du cuir chevelu et des longueurs, 20 ml."
      : "Sérum capillaire LUXE ORYVEA PREMIUM pour homme — soin du cuir chevelu et des longueurs, 20 ml.";
    const absoluteImage = (imgs.single || "").startsWith("http") ? imgs.single : `${window.location.origin}${imgs.single || ""}`;

    document.title = `${productName} | Luxe Oryvea`;
    const setMeta = (name, content, attr = "name") => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("og:title", productName, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:image", absoluteImage, "property");
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = canonical;

    let script = document.head.querySelector("#luxe-oryvea-product-schema");
    if (!script) { script = document.createElement("script"); script.id = "luxe-oryvea-product-schema"; script.type = "application/ld+json"; document.head.appendChild(script); }
    const availability = out ? "https://schema.org/OutOfStock" : "https://schema.org/InStock";
    const ratingCount = publishedReviews.length;
    const ratingValue = ratingCount ? publishedReviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / ratingCount : 0;
    const reviewSchema = publishedReviews.map((review) => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": review.name },
      "reviewRating": { "@type": "Rating", "ratingValue": Number(review.rating), "bestRating": 5, "worstRating": 1 },
      "reviewBody": review.text
    }));
    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": productName,
      "description": description,
      "image": [absoluteImage],
      "brand": { "@type": "Brand", "name": "LUXE ORYVEA" },
      "sku": variant.key,
      "category": "Hair Care",
      ...(ratingCount ? {
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": Number(ratingValue.toFixed(2)), "reviewCount": ratingCount, "bestRating": 5, "worstRating": 1 },
        "review": reviewSchema
      } : {}),
      "offers": {
        "@type": "Offer",
        "url": canonical,
        "priceCurrency": "EUR",
        "price": variant.unit.toFixed(2),
        "availability": availability,
        "itemCondition": "https://schema.org/NewCondition",
        "hasMerchantReturnPolicy": {
          "@type": "MerchantReturnPolicy",
          "applicableCountry": "FR",
          "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
          "merchantReturnDays": 14,
          "returnMethod": "https://schema.org/ReturnByMail"
        },
        "shippingDetails": [
          {
            "@type": "OfferShippingDetails",
            "shippingRate": {
              "@type": "MonetaryAmount",
              "value": 5.90,
              "currency": "EUR"
            },
            "shippingDestination": {
              "@type": "DefinedRegion",
              "addressCountry": "FR"
            },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "handlingTime": {
                "@type": "QuantitativeValue",
                "minValue": 0,
                "maxValue": 1,
                "unitCode": "DAY"
              },
              "transitTime": {
                "@type": "QuantitativeValue",
                "minValue": 2,
                "maxValue": 4,
                "unitCode": "DAY"
              }
            }
          },
          {
            "@type": "OfferShippingDetails",
            "shippingRate": {
              "@type": "MonetaryAmount",
              "value": 4.90,
              "currency": "EUR"
            },
            "shippingDestination": {
              "@type": "DefinedRegion",
              "addressCountry": "FR"
            },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "handlingTime": {
                "@type": "QuantitativeValue",
                "minValue": 0,
                "maxValue": 1,
                "unitCode": "DAY"
              },
              "transitTime": {
                "@type": "QuantitativeValue",
                "minValue": 2,
                "maxValue": 4,
                "unitCode": "DAY"
              }
            }
          }
        ]
      }
    };
    script.textContent = JSON.stringify(productSchema);
  }, [effectiveUniverse, fixedUniverse, imgs.single, variant.key, variant.unit, stock, stockKnown, out, setUniverse, publishedReviews]);

  useEffect(() => {
    api.get("/products").then((r) => { setCatalog(r.data || []); setCatalogLoaded(true); }).catch(() => setCatalogLoaded(true));
    api.get("/reviews").then((r) => setPublishedReviews((r.data || []).filter((review) => review.universe === effectiveUniverse))).catch(() => setPublishedReviews([]));
  }, [effectiveUniverse]);

  const addToCart = (go) => {
    if (out) { toast.error("Produit épuisé"); return; }
    if (q > stock) { toast.error(`Stock disponible : ${stock}`); setQ(Math.max(1, stock)); return; }
    const added = add({ key: variant.key, label: `${variant.label} — ${variant.sub}`, unit: variant.unit, image: variantImg, universe }, q);
    toast.success(`${variant.label} ${t("product.added")}`);
    if (go) nav("/panier");
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 grid md:grid-cols-2 gap-14">
        <div>
          <div className="relative mb-4 overflow-hidden" style={{ border: "1px solid var(--lo-border-rose)", background: GALLERY[active].includes("board") ? (effectiveUniverse === "femme" ? "#f7ecec" : "#0a0a0a") : "var(--lo-surface-2)" }}>
            <img src={GALLERY[active]} alt="Sérum Capillaire" decoding="async" className={`w-full h-[520px] ${GALLERY[active].includes("board") ? "object-contain" : "object-cover"}`} data-testid="product-main-img" />
          </div>
          <div className="grid grid-cols-4 gap-3">
            {GALLERY.map((g, i) => (
              <button key={i} onClick={() => setActive(i)} className="overflow-hidden" style={{ border: `1px solid ${active === i ? "var(--lo-rose)" : "var(--lo-border)"}`, background: g.includes("board") ? (effectiveUniverse === "femme" ? "#f7ecec" : "#0a0a0a") : "transparent" }} data-testid={`product-thumb-${i}`}>
                <img src={g} alt="" loading="lazy" decoding="async" className={`w-full h-20 ${g.includes("board") ? "object-contain" : "object-cover"}`} />
              </button>
            ))}
          </div>
        </div>

        <div>
          {out ? (
            <span className="inline-flex items-center px-3 py-1 mb-5 text-[10px] uppercase tracking-wider" style={{ background: "#3a2a2a", color: "#e8b4b4" }} data-testid="product-out">Épuisé</span>
          ) : (
            <StockBadge className="mb-5" />
          )}
          <p className="overline mb-2">{PRODUCT.name}</p>
          <h1 className="font-display text-5xl md:text-6xl mb-3" style={{ fontWeight: 300 }}>{PRODUCT.subtitle}</h1>
          <p className="text-sm mb-6" style={{ color: "var(--lo-muted)" }}>{PRODUCT.volume}</p>

          <div className="flex items-end gap-4 mb-7">
            <span className="font-display text-4xl rose-gold-text">{eur(variant.unit)}</span>
            {variant.compareAt && <span className="text-lg line-through mb-1" style={{ color: "var(--lo-muted)" }}>{eur(variant.compareAt)}</span>}
          </div>

          <p className="text-sm leading-relaxed mb-7" style={{ color: "var(--lo-muted)" }}>
            {t("product.description")}
          </p>

          <p className="overline mb-3">{t("product.choose_format")}</p>
          <div className="grid grid-cols-2 gap-3 mb-7">
            {Object.entries(VARIANTS).map(([key, v]) => {
              const sel = key === variantKey;
              const save = v.compareAt ? Math.round((v.compareAt - v.unit)) : 0;
              return (
                <button key={key} onClick={() => { setVariantKey(key); setQ(1); }} className="relative text-left p-4 transition-all" style={{ border: `1px solid ${sel ? "var(--lo-rose)" : "var(--lo-border)"}`, background: sel ? "rgba(198,127,137,0.08)" : "transparent" }} data-testid={`variant-${key}`}>
                  {v.compareAt && <span className="absolute -top-2.5 right-3 badge-save text-[9px] px-2 py-0.5 uppercase">{t("product.save")} {save} €</span>}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0" style={{ border: `1px solid ${sel ? "var(--lo-rose)" : "var(--lo-border)"}`, background: sel ? "var(--lo-rose)" : "transparent" }}>{sel && <Check size={11} stroke="#100708" />}</span>
                    <span className="font-display text-lg" style={{ fontWeight: 400 }}>{v.label}</span>
                  </div>
                  <p className="text-xs" style={{ color: "var(--lo-muted)" }}>{v.sub} · {eur(v.unit)}</p>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-6 mb-6">
            <div className="flex items-center border" style={{ borderColor: "var(--lo-border-rose)" }}>
              <button className="w-12 h-12 flex items-center justify-center disabled:opacity-30" disabled={!stockKnown || out} onClick={() => setQ(Math.max(1, q - 1))} data-testid="qty-minus"><Minus size={16} /></button>
              <span className="w-12 text-center font-display text-xl" data-testid="qty-value">{q}</span>
              <button className="w-12 h-12 flex items-center justify-center disabled:opacity-30" disabled={!stockKnown || out || q >= Math.min(10, stock)} onClick={() => setQ(Math.min(10, stock, q + 1))} data-testid="qty-plus"><Plus size={16} /></button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <button className="btn-ghost h-14 px-8 flex-1 disabled:opacity-40 disabled:cursor-not-allowed" disabled={!stockKnown || out} onClick={() => addToCart(false)} data-testid="add-to-cart-btn">{out ? "Épuisé" : !stockKnown ? "Vérification…" : t("common.add_to_cart")}</button>
            <button className="btn-primary h-14 px-8 flex-1 disabled:opacity-40 disabled:cursor-not-allowed" disabled={!stockKnown || out} onClick={() => addToCart(true)} data-testid="buy-now-btn">{out ? "Épuisé" : !stockKnown ? "Vérification…" : t("common.order")}</button>
          </div>

          <div className="space-y-3 text-sm" style={{ color: "var(--lo-muted)" }}>
            <p className="flex items-center gap-3"><Truck size={16} stroke="var(--lo-rose)" /> {t("product.free_ship")}</p>
            <p className="flex items-center gap-3"><ShieldCheck size={16} stroke="var(--lo-rose)" /> {t("product.secure")}</p>
            <p className="flex items-center gap-3"><Sparkles size={16} stroke="var(--lo-rose)" /> {t("product.adapted")}</p>
          </div>

          <div className="mt-7 max-w-xl">
            <PaymentTrust compact />
          </div>
        </div>
      </div>

      <section className="py-20 md:py-28 border-t" style={{ borderColor: "var(--lo-border)", background: "var(--lo-surface)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><p className="overline mb-4">{t("product.actives")}</p>
            <h2 className="font-display text-3xl sm:text-4xl mb-12" style={{ fontWeight: 300 }}>{t("product.ingredients_title")}</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {INGREDIENTS.map((ing, i) => (
              <Reveal key={ing.name} delay={i * 0.04}>
                <div className="card-lux p-5 text-center h-full" data-testid={`product-ingredient-${i}`}>
                  <Icon name={ing.icon} size={22} stroke="var(--lo-rose)" strokeWidth={1} className="mx-auto mb-3" />
                  <h3 className="font-display text-lg mb-1" style={{ fontWeight: 400 }}>{ing.name}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: "var(--lo-muted)" }}>{ing.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t" style={{ borderColor: "var(--lo-border)" }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="overline mb-3">{t("product.fragrance_title")}</p>
          <h2 className="font-display text-3xl sm:text-4xl mb-4" style={{ fontWeight: 300 }}>{t("product.fragrance_name")}</h2>
          <p className="text-sm leading-relaxed" style={{ color: "var(--lo-muted)" }}>{t("product.fragrance_text")}</p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16">
          <div>
            <p className="overline mb-4">{t("product.benefits")}</p>
            <div className="space-y-6">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex gap-4">
                  <Icon name={b.icon} size={20} stroke="var(--lo-rose)" strokeWidth={1} className="mt-1 shrink-0" />
                  <div><h3 className="font-display text-xl" style={{ fontWeight: 400 }}>{b.title}</h3>
                    <p className="text-sm" style={{ color: "var(--lo-muted)" }}>{b.text}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="overline mb-4">{t("product.usage")}</p>
            <div className="space-y-6">
              {STEPS.map((s) => (
                <div key={s.n} className="flex gap-4">
                  <span className="font-display text-3xl rose-gold-text italic">{s.n}</span>
                  <div><h3 className="font-display text-xl" style={{ fontWeight: 400 }}>{s.title}</h3>
                    <p className="text-sm" style={{ color: "var(--lo-muted)" }}>{s.text}</p></div>
                </div>
              ))}
            </div>
            <div className="card-lux p-6 mt-10">
              <p className="overline mb-2">{t("product.shipping_info")}</p>
              <p className="text-sm" style={{ color: "var(--lo-muted)" }}>{t("product.shipping_text")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 border-t" style={{ borderColor: "var(--lo-border)" }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="overline mb-8">{t("product.faq_title")}</p>
          <FaqAccordion />
        </div>
      </section>

      <Reviews />

      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t px-4 py-3 flex items-center gap-3 backdrop-blur-xl" style={{ borderColor: "var(--lo-border-rose)", background: "color-mix(in srgb, var(--lo-bg) 92%, transparent)" }} data-testid="product-sticky-bar">
        <div className="leading-tight">
          <p className="font-display text-xl" style={{ color: "var(--lo-rose-hi)" }}>{eur(variant.unit)}</p>
        </div>
        <button className="btn-primary flex-1 h-12" onClick={() => addToCart(true)} data-testid="product-sticky-add">{t("common.order")}</button>
      </div>
      <div className="h-16 md:hidden" />
    </>
  );
}
